import { Box, Divider, Grid2 } from '@mui/material';
import React from 'react';

interface ResponsiveGridProps<T extends React.ReactNode> {
  items: T[];
}

function chunkItems<T>(items: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    result.push(items.slice(i, i + size));
  }
  return result;
}

function ResponsiveGrid<T extends React.ReactNode>({
  items,
}: ResponsiveGridProps<T>) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [columns, setColumns] = React.useState(1);

  React.useEffect(() => {
    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;

      // Estimate how many columns based on width & desired min item size
      const minItemWidth = 140; // px
      const maxColumns = Math.max(1, Math.floor(width / minItemWidth));
      setColumns(maxColumns);
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const rows = chunkItems(items, columns);

  return (
    <Box ref={containerRef} sx={{ p: 2 }}>
      {rows.map((row, i) => (
        <Grid2 container spacing={2} justifyContent={'space-evenly'} key={i}>
          {row.map((item, j) => (
            <>
              {j > 0 && (
                <Grid2>
                  <Divider orientation="vertical" />
                </Grid2>
              )}
              <Grid2 key={j}>
                <Box
                  sx={{
                    minWidth: 100,
                    textAlign: 'center',
                  }}
                >
                  {item}
                </Box>
              </Grid2>
            </>
          ))}
        </Grid2>
      ))}
    </Box>
  );
}

export default ResponsiveGrid;
